import { getFalseRangeDisplayValue } from "helpers/utility/utilities";
import { useEffect, useRef, useState } from "react";
import Slider from "rc-slider";

import styles from "./styles.module.scss";
import classNames from "classnames";
import { RANGE_QUERY_MIN_VALUE } from "../../../../helpers/constants/queries";

const MAX = 1;

export interface RangeInputProps {
  title: string;
  range: [number, number];
  showActualValues?: boolean;
  onChange: (range: [number, number]) => void;
  minValue?: number;
}

export default function RangeInput({
  title,
  range,
  showActualValues = false,
  onChange,
  minValue = RANGE_QUERY_MIN_VALUE,
}: RangeInputProps) {
  const [activeHandle, setActiveHandle] = useState<"min" | "max" | null>(null);
  const [currentRange, setCurrentRange] = useState<[number, number]>(range);
  const [falseValue, setFalseValue] = useState<number[]>([
    getFalseRangeDisplayValue(range[0], minValue),
    getFalseRangeDisplayValue(range[1], minValue),
  ]);
  const minTooltipRef = useRef<HTMLDivElement>(null);
  const maxTooltipRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLInputElement>(null);

  const dynamicMin = showActualValues ? 0 : minValue;

  const minTooltipClassnames = classNames(styles.minTooltip, {
    [styles.activeTooltip]: activeHandle === "min",
  });

  const maxTooltipClassnames = classNames(styles.maxTooltip, {
    [styles.activeTooltip]: activeHandle === "max",
  });

  const handleChange = (value: [number, number]) => {
    // Compare to previous state to determine which one changed
    const [prevMin, prevMax] = currentRange;
    const [newMin, newMax] = value;
    if (!showActualValues) {
      //The value displayed to users should always be from 0 to 1
      // so we convert the values 0.8 to 0 and so on just for display purposes
      setFalseValue([
        getFalseRangeDisplayValue(newMin, minValue),
        getFalseRangeDisplayValue(newMax, minValue),
      ]);
    }

    if (newMin !== prevMin) {
      setActiveHandle("min");
    } else if (newMax !== prevMax) {
      setActiveHandle("max");
    }

    setCurrentRange(value);
  };

  useEffect(() => {
    if (sliderRef.current && currentRange) {
      const maxTooltip = maxTooltipRef.current;
      const minTooltip = minTooltipRef.current;
      const slider = sliderRef.current;

      const sliderWidth = slider.offsetWidth;

      if (minTooltip) {
        const tooltipWidth = minTooltip.offsetWidth;

        const percentage = (currentRange[0] - dynamicMin) / (MAX - dynamicMin);
        const leftPosition = percentage * sliderWidth - tooltipWidth / 2;

        minTooltip.style.left = `${leftPosition}px`;
      }

      if (maxTooltip) {
        const tooltipWidth = maxTooltip.offsetWidth;

        const percentage = (currentRange[1] - dynamicMin) / (MAX - dynamicMin);
        const leftPosition = percentage * sliderWidth - tooltipWidth / 2;

        maxTooltip.style.left = `${leftPosition}px`;
      }
    }
  }, [currentRange, dynamicMin]);

  return (
    <div className={classNames(styles.rangeContainer, "rangeContainer")}>
      <div className={classNames(styles.title, "rangeTitle")}>{title}</div>
      <div className={classNames(styles.label, "rangeLabel")}>0</div>
      <div className={styles.sliderWrapper} ref={sliderRef}>
        <Slider
          autoFocus
          range
          min={dynamicMin}
          max={MAX}
          step={0.01}
          value={currentRange}
          onChange={(value: number | number[]) =>
            handleChange(value as [number, number])
          }
          onChangeComplete={(value: number | number[]) =>
            onChange(value as [number, number])
          }
          className={styles.slider}
        />
        <div className={minTooltipClassnames} ref={minTooltipRef}>
          {showActualValues ? currentRange[0] : falseValue[0]}
        </div>
        <div className={maxTooltipClassnames} ref={maxTooltipRef}>
          {showActualValues ? currentRange[1] : falseValue[1]}
        </div>
      </div>
      <div className={styles.label}>1</div>
    </div>
  );
}
